/**
 * @file SubSitioCard.js
 * @description Tarjeta de resumen de vehículos por subsitio
 * @module components/cards/SubSitioCard
 * 
 * Características principales:
 * - Vista resumen de vehículos RFID en un subsitio específico
 * - Indicador de estado con punto de color en esquina superior derecha
 * - Tipo de subsitio identificado y coloreado (patio, taller, showroom, etc.)
 * - Desglose por marca (máximo 4 principales, ordenadas por cantidad)
 * - Logos de marcas cuando están disponibles
 * - Estados vacíos con diseño diferenciado
 * - Muestra sitio padre para contexto
 * - Clickable para navegar a vista de detalle
 * 
 * Jerarquía de navegación:
 * Estados → Sitios → Subsitios (este componente) → Detalle
 * 
 * Tipos de subsitio soportados:
 * - patio: Área principal de almacenamiento
 * - taller: Área de servicio y mantenimiento
 * - showroom: Área de exhibición interna
 * - showroom_exterior: Área de exhibición externa
 * - sala_entrega: Área de entrega a clientes
 * - otro: Áreas no categorizadas
 * 
 * Niveles de ocupación (semáforo):
 * - Verde (0-50%): Ocupación baja
 * - Amarillo (51-80%): Ocupación media
 * - Rojo (81-100%): Ocupación alta
 * - Gris: Subsitio vacío
 * 
 * Diferencias con SitioCard:
 * - No muestra barra de progreso (menos énfasis en capacidad)
 * - Muestra sitio padre en lugar de estado
 * - Badge de tipo en lugar de categoría
 * - Más compacto y simple
 */

import React, { useState, useMemo, useCallback } from 'react';
import { MapPin, Car, Building, Package, AlertCircle } from 'lucide-react';
import { getSubSitioType, getSubSitioColor } from '../../data/patiosData';

const BRANDS_PER_PAGE = 4;

/**
 * Mapeo de logos de marcas
 * URLs de imágenes de logos ubicadas en /public/logos/
 * Solo incluye marcas que tienen logo disponible
 * 
 * @const {Object} marcasLogos
 */
const marcasLogos = {
  'FORD': '/logos/ford.png',
  'LINCOLN': '/logos/lincoln.png',
  'GWM': '/logos/gwm.png',
  'MAZDA': '/logos/mazda.png',
};

/**
 * Componente SubSitioCard - Tarjeta de resumen por subsitio
 * 
 * Muestra un resumen ejecutivo de los vehículos detectados por RFID en un
 * subsitio específico. Es el nivel más granular antes de la vista de detalle.
 * 
 * Flujo de datos:
 * 1. Recibe objeto subSitio con datos agregados
 * 2. Determina tipo de subsitio mediante getSubSitioType()
 * 3. Aplica colores según tipo mediante getSubSitioColor()
 * 4. Muestra top 4 marcas ordenadas por cantidad
 * 5. Al hacer click, navega a PatioCard con vista de detalle
 * 
 * 
 * @component
 * @param {Object} props
 * @param {Object} props.subSitio - Objeto con datos del subsitio
 * @param {string} props.subSitio.nombre - Nombre del subsitio (ej: "Patio", "Taller")
 * @param {string} props.subSitio.sitio - Nombre del sitio padre
 * @param {string} props.subSitio.estado - Estado geográfico
 * @param {number} props.subSitio.totalVehiculos - Total de vehículos en el subsitio
 * @param {number} [props.subSitio.porcentajeOcupacion=0] - Porcentaje de ocupación (0-100)
 * @param {Object} props.subSitio.marcas - Objeto {marca: cantidad} con desglose
 * @param {boolean} props.subSitio.estaVacio - Si el subsitio no tiene vehículos
 * @param {Function} props.onSubSitioClick - Callback al hacer click en la tarjeta
 * @returns {JSX.Element} Tarjeta del subsitio
 */
const SubSitioCard = ({ subSitio, onSubSitioClick }) => {
  /**
   * Determina el tipo de subsitio basándose en su nombre
   * 
   * Tipos posibles:
   * - "patio": Área principal de almacenamiento
   * - "taller": Área de servicio
   * - "showroom": Área de exhibición
   * - "showroom_exterior": Exhibición externa
   * - "sala_entrega": Área de entrega
   * - "otro": No identificado
   * 
   * @const {string} tipo
   */
  const tipo = getSubSitioType(subSitio.nombre);

  /**
   * Obtiene clase de color según el tipo de subsitio
   * 
   * Cada tipo tiene su color identificador:
   * - Patio: Azul
   * - Taller: Naranja
   * - Showroom: Verde
   * - Sala entrega: Morado
   * - Otro: Gris
   * 
   * @const {string} colorClass
   */
  const colorClass = getSubSitioColor(tipo);

  /**
   * Determina si el subsitio está vacío (sin vehículos)
   */
  const estaVacio = subSitio.totalVehiculos === 0;

  /**
   * Porcentaje de ocupación del subsitio (0-100)
   */
  const porcentajeOcupacion = subSitio.porcentajeOcupacion || 0;

  // Carousel de marcas
  const sortedMarcas = useMemo(() =>
    Object.entries(subSitio.marcas || {}).sort((a, b) => b[1] - a[1]),
    [subSitio.marcas]
  );
  const totalPages = Math.ceil(sortedMarcas.length / BRANDS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(0);
  const prevPage = useCallback((e) => { e.stopPropagation(); setCurrentPage(p => (p - 1 + totalPages) % totalPages); }, [totalPages]);
  const nextPage = useCallback((e) => { e.stopPropagation(); setCurrentPage(p => (p + 1) % totalPages); }, [totalPages]);

  /**
   * Obtiene color del indicador de estado (punto en esquina)
   * 
   * Sistema de semáforo:
   * - Gris: Subsitio vacío
   * - Verde (0-50%): Ocupación baja
   * - Amarillo (51-80%): Ocupación media
   * - Rojo (81-100%): Ocupación alta
   * 
   * @returns {string} Clase Tailwind de color de fondo
   */
  const getStatusIndicatorColor = () => {
    if (estaVacio) return 'bg-gray-400';
    if (porcentajeOcupacion <= 50) return 'bg-green-500';
    if (porcentajeOcupacion <= 80) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div
      className={`border rounded-lg p-3 sm:p-4 transition-all duration-200 cursor-pointer sm:hover:scale-[1.02] transform relative ${estaVacio
          ? 'bg-gray-50 border-gray-200 hover:shadow-md hover:bg-gray-100'
          : 'bg-white border-gray-300 hover:shadow-lg hover:bg-gray-50'
        } flex flex-col h-full`}
      onClick={() => onSubSitioClick && onSubSitioClick(subSitio)}
    >

      {/* ===== INDICADOR DE ESTADO (punto de color) ===== */}
      {/* Posicionado en esquina superior derecha para referencia rápida */}
      <div className={`absolute top-3 right-3 w-3 h-3 rounded-full ${getStatusIndicatorColor()}`} />

      {/* ===== HEADER DEL SUBSITIO ===== */}
      <div className="flex items-center justify-between mb-3 pr-4">
        {/* Nombre del subsitio con ícono */}
        <div className="flex items-center space-x-2 min-w-0">
          <Building className={`w-4 h-4 shrink-0 ${estaVacio ? 'text-gray-500' : 'text-gray-600'}`} />
          <h3 className={`font-semibold text-sm sm:text-base truncate ${estaVacio ? 'text-gray-600' : 'text-gray-900'}`}>
            {subSitio.nombre}
          </h3>
        </div>

        {/* Badge de tipo de subsitio (con primera letra mayúscula) */}
        <span className={`text-xs px-2 py-0.5 sm:py-1 rounded-full shrink-0 ml-1 ${colorClass}`}>
          {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
        </span>
      </div>

      {/* ===== INFORMACIÓN DEL SITIO PADRE ===== */}
      {/* Muestra a qué sitio pertenece este subsitio */}
      <div className="flex items-center space-x-1 mb-3 text-xs text-gray-500">
        <MapPin className="w-3 h-3" />
        <span>{subSitio.sitio}</span>
      </div>

      {/* ===== TOTAL DE VEHÍCULOS ===== */}
      {/* Box destacado con contador total */}
      <div className="flex items-center justify-between mb-3 bg-gray-50 rounded px-2 py-1">
        <div className="flex items-center space-x-1">
          <Car className="w-4 h-4 text-gray-600" />
          <span className="text-sm text-gray-600">Total vehículos</span>
        </div>
        <span className="text-sm font-bold text-gray-900">{subSitio.totalVehiculos}</span>
      </div>

      {/* ===== CONTENIDO PRINCIPAL: MARCAS O ESTADO VACÍO ===== */}
      {estaVacio ? (
        /* SUBSITIO VACÍO: Diseño especial sin datos */
        <div className="space-y-3 mb-3">
          <div className="flex items-center justify-center py-4 border-2 border-dashed border-gray-300 rounded-lg">
            <div className="text-center">
              <Package className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500 font-medium">Sin vehículos</p>
              <p className="text-xs text-gray-400">Subsitio disponible</p>
            </div>
          </div>
        </div>
      ) : (
        /* SUBSITIO CON VEHÍCULOS: Desglose por marcas con carousel */
        <div className="mb-6">
          {/* Carousel */}
          <div className="overflow-hidden">
            <div
              style={{
                display: 'flex',
                transform: `translateX(-${currentPage * 100}%)`,
                transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {Array.from({ length: totalPages }).map((_, pageIdx) => {
                const pageBrands = sortedMarcas.slice(
                  pageIdx * BRANDS_PER_PAGE,
                  (pageIdx + 1) * BRANDS_PER_PAGE
                );
                return (
                  <div key={pageIdx} style={{ minWidth: '100%' }} className="space-y-2">
                    {pageBrands.map(([marca, cantidad]) => (
                      <div key={marca} className="flex items-center justify-between text-sm">
                        <div className="flex items-center space-x-2">
                          {marcasLogos[marca] && (
                            <img
                              src={marcasLogos[marca]}
                              alt={`${marca} logo`}
                              className="w-[18px] h-[18px] object-contain"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          )}
                          <span className="text-gray-700 text-xs">{marca}</span>
                        </div>
                        <span className="font-medium text-gray-900 text-xs">{cantidad}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Paginación — solo si hay más de una página */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-1.5" onClick={e => e.stopPropagation()}>
              <button onClick={prevPage} aria-label="Página anterior"
                className="w-5 h-5 flex items-center justify-center rounded text-[#1d2e61] hover:bg-[#1d2e61]/10 transition-all">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <span className="text-[10px] font-medium text-gray-500 tabular-nums">
                {currentPage + 1}/{totalPages}
              </span>
              <button onClick={nextPage} aria-label="Página siguiente"
                className="w-5 h-5 flex items-center justify-center rounded text-[#1d2e61] hover:bg-[#1d2e61]/10 transition-all">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          )}
        </div>
      )}

      {/* ===== FOOTER CON INDICADOR DE ACCIÓN ===== */}
      <div className="mt-auto pt-3 border-t border-gray-100">
        <p className={`text-xs text-center flex items-center justify-center space-x-1 ${estaVacio ? 'text-gray-400' : 'text-gray-500'
          }`}>
          {estaVacio ? (
            /* Mensaje para subsitio vacío */
            <>
              <span>Subsitio disponible</span>
              <AlertCircle className="w-3 h-3" />
            </>
          ) : (
            /* Mensaje para subsitio con vehículos */
            <>
              <span>Ver vehículos</span>
              {/* Flecha derecha */}
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </>
          )}
        </p>
      </div>
    </div>
  );
};

export default SubSitioCard;